import {ChoiceKey, ChoiceModel} from 'models/configuration/ChoiceModel'
import {OptionKey} from 'models/configuration/OptionModel'
import {PackKey, PackModel} from 'models/configuration/PackModel'
import {TourKey, TourModel} from 'models/configuration/TourModel'
import {TrackModel} from 'models/configuration/TrackModel'
import {EventModel} from 'models/EventModel'
import {DetailsInfoState} from 'models/registration/steps/DetailsStepModel'
import {Memoize} from 'util/memoize'
import {GiftStepState} from './registration/steps/GiftStepModel'

export type UserState = {
	id: number
	event_id: number
	registration_id: number
	email: string
	name: string
	reference: string
	packs: Array<PackKey>
	tours: Array<TourKey>
	gift: GiftStepState
	options: Record<OptionKey, ChoiceKey>
	details: DetailsInfoState
	type: string
	login_link: string
	created_at: string
	updated_at: string
	amount_payed: number
	amount_payed_excl: number
	confirmation?: {
		id: number
		price: number
		price_excl: number
	}
}
export class UserModel {
	state: UserState
	event: EventModel

	constructor(state: UserState, event: EventModel) {
		this.state = state
		this.event = event
	}

	@Memoize()
	get packs(): Array<PackModel> {
		const packKeys = new Set(this.state.packs)
		return this.event.packs.filter(pack => packKeys.has(pack.key))
	}

	@Memoize()
	get choices(): Array<ChoiceModel> {
		return this.event.options
			.map(option => option.getChoiceFor(this))
			.filter(choice => !!choice)
	}

	@Memoize()
	get sharedChoices(): Array<ChoiceModel> {
		return this.choices.filter(choice => choice.option.tracks.length > 1)
	}

	@Memoize()
	get tracks(): Array<TrackModel> {
		let tracks: Array<TrackModel> = []
		this.packs.forEach(pack => {
			pack.tracks.forEach(track => {
				if (!new Set(tracks).has(track)) tracks = [...tracks, track]
			})
		})
		return tracks
	}

	@Memoize()
	get hasEnabledGift(): boolean {
		return this.state.gift?.consent === 'accept'
	}

	@Memoize()
	get giftAddressExport() {
		const row = {
			gift_lastname: '',
			gift_firstname: '',
			gift_postalcode: '',
			gift_prefecture: '',
			gift_city: '',
			gift_address: '',
			gift_company: '',
			gift_building: '',
			gift_phone: ''
		}
		if (!this.state.gift || this.state.gift.consent === 'decline') return row
		return Object.fromEntries(
			Object.keys(row).map(key => [key, this.state.gift[key.split('_')[1]]])
		) as typeof row
	}

	@Memoize()
	get name(): string {
		return this.state.name
	}

	@Memoize()
	get function(): string {
		return this.state.details.function
	}

	@Memoize()
	get company(): string {
		return this.state.details.company
	}

	@Memoize()
	get email(): string {
		return this.state.email
	}

	@Memoize()
	get tours(): Array<TourModel> {
		const tourKeys = new Set(this.state.tours)
		return this.event.tours.filter(tour => tourKeys.has(tour.key))
	}

	@Memoize()
	getChoicesFor(track: TrackModel): Array<ChoiceModel> {
		return []
	}

	getToursFor(track: TrackModel): Array<TourModel> {
		const toursSet = new Set(this.tours)
		return track.tours.filter(tour => toursSet.has(tour))
	}
}
